import React, {useCallback, useEffect, useState} from 'react'
import {Alert, Badge, Button, Form, Modal, Nav, Spinner, Tab} from 'react-bootstrap'
import {FormattedMessage, defineMessages, useIntl} from 'react-intl'
import {toast} from 'react-toastify'
import {URL as BACKEND_URL} from '../../../gql/client'
import {STORAGE_NAMES} from '../../../helpers/consts'
import {parseId} from '../../../helpers'
import {LeadTagManager} from '../../leads/components/LeadTagManager'
import {DrawerEditLead} from '../../leads/components/DrawerEditLead'
import {IInfoLead} from '../../leads/leadTypes'

interface Props {
  isOpen: boolean
  onClose: () => void
  stage: {id: string; name: string}
  configId?: number | null
  onChanged?: () => void
  /** Sin `change_google_leads_webhook` el modal queda en solo lectura. */
  canEdit: boolean
}

interface TagOption {
  id: string
  name: string
  color: string
}

interface WebhookConfig {
  id: number
  token: string
  google_key: string
  webhook_url: string
  is_active: boolean
  description: string
  type_lead: 'PERSONAL' | 'COMERCIAL'
  lead_source_id: string | null
  lead_source_name: string | null
  campus_id: string | null
  campus_name: string | null
  tags: TagOption[]
  allow_test_leads: boolean
  last_triggered_at: string | null
  last_lead_id: number | null
  leads_count: number
}

interface LeadSourceCategory {
  id: string
  name: string
  sources: {id: string; name: string}[]
}

interface Options {
  categories: LeadSourceCategory[]
  campuses: {id: string; name: string}[]
  tags: TagOption[]
}

type CopyField = 'url' | 'key' | null

function authHeaders(): HeadersInit {
  const token = localStorage.getItem(STORAGE_NAMES.TOKEN) || ''
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  }
}

const emdash = '—'

const wM = defineMessages({
  readOnly: {
    id: 'leads.webhook.readOnly',
    defaultMessage: 'You can view this webhook but not change it. Editing requires the change permission for this integration.',
  },
  googleTitle: {id: 'leads.webhook.googleTitle', defaultMessage: 'Google Ads Webhook'},
  googleInstructions: {id: 'leads.webhook.googleInstructions', defaultMessage: 'Copy these values into your Google Ads Lead Form \u2192 Webhook integration settings.'},
  webhookUrl: {id: 'leads.webhook.webhookUrl', defaultMessage: 'Webhook URL'},
  keyGoogleField: {id: 'leads.webhook.keyGoogleField', defaultMessage: 'Key (Google Ads field)'},
  leadsReceived: {id: 'leads.webhook.leadsReceived', defaultMessage: 'Leads received'},
  lastTriggered: {id: 'leads.webhook.lastTriggered', defaultMessage: 'Last triggered'},
  viewLastLead: {id: 'leads.webhook.viewLastLead', defaultMessage: 'View last lead'},
  statusLabel: {id: 'common.field.status', defaultMessage: 'Status'},
  description: {id: 'leads.webhook.description', defaultMessage: 'Description'},
  descriptionPlaceholder: {id: 'leads.webhook.descriptionPlaceholder', defaultMessage: 'e.g. Summer campaign — Miami campus'},
  allowTestLeads: {id: 'leads.webhook.allowTestLeads', defaultMessage: 'Allow test leads'},
  allowTestLeadsHint: {id: 'leads.webhook.allowTestLeadsHint', defaultMessage: 'Google Ads sends test submissions with is_test=true. Enable this to create leads from those too (useful while testing the integration).'},
  updateSettings: {id: 'leads.webhook.updateSettings', defaultMessage: 'Update settings'},
  createForStage: {id: 'leads.webhook.createForStage', defaultMessage: 'Create webhook for this stage'},
  leadSourceCategory: {id: 'leads.webhook.leadSourceCategory', defaultMessage: 'Lead Source Category'},
  selectCategory: {id: 'leads.webhook.selectCategory', defaultMessage: '\u2014 Select category \u2014'},
  selectSource: {id: 'leads.webhook.selectSource', defaultMessage: '\u2014 Select source \u2014'},
  campus: {id: 'leads.webhook.campus', defaultMessage: 'Campus'},
  selectCampus: {id: 'leads.webhook.selectCampus', defaultMessage: '\u2014 Select campus \u2014'},
  leadType: {id: 'leads.webhook.leadType', defaultMessage: 'Lead type'},
  autoAssignTags: {id: 'leads.webhook.autoAssignTags', defaultMessage: 'Auto-assign tags'},
  manageTags: {id: 'leads.webhook.manageTags', defaultMessage: 'Manage tags'},
  tagsSelected: {id: 'leads.webhook.tagsSelected', defaultMessage: '{count, plural, one {# tag selected \u2014 applied automatically to every lead from this webhook} other {# tags selected \u2014 applied automatically to every lead from this webhook}}'},
  webhookActive: {id: 'leads.webhook.webhookActive', defaultMessage: 'Webhook active'},
  dangerZone: {id: 'leads.webhook.dangerZone', defaultMessage: 'Danger zone'},
  regenTokenKey: {id: 'leads.webhook.regenTokenKey', defaultMessage: 'Regenerate token & key'},
  regenWarnGoogle: {id: 'leads.webhook.regenWarnGoogle', defaultMessage: 'This invalidates the current URL. Update Google Ads.'},
  confirmRegenerate: {id: 'leads.webhook.confirmRegenerate', defaultMessage: 'Confirm regenerate'},
  deleteWebhook: {id: 'leads.webhook.deleteWebhook', defaultMessage: 'Delete webhook'},
  deleteConfirm: {id: 'leads.webhook.deleteConfirm', defaultMessage: 'Delete this webhook config?'},
  yesDelete: {id: 'leads.webhook.yesDelete', defaultMessage: 'Yes, delete'},
  saving: {id: 'leads.webhook.saving', defaultMessage: 'Saving\u2026'},
  updateWebhook: {id: 'leads.webhook.updateWebhook', defaultMessage: 'Update webhook'},
  createWebhook: {id: 'leads.webhook.createWebhook', defaultMessage: 'Create webhook'},
  errLoadConfig: {id: 'leads.webhook.errLoadConfig', defaultMessage: 'Error loading webhook config'},
  errNetworkLoad: {id: 'leads.webhook.errNetworkLoad', defaultMessage: 'Network error loading config'},
  selectLeadSourceWarn: {id: 'leads.webhook.selectLeadSourceWarn', defaultMessage: 'Please select a lead source'},
  selectCampusWarn: {id: 'leads.webhook.selectCampusWarn', defaultMessage: 'Please select a campus'},
  webhookUpdated: {id: 'leads.webhook.webhookUpdated', defaultMessage: 'Webhook updated'},
  webhookCreated: {id: 'leads.webhook.webhookCreated', defaultMessage: 'Webhook created'},
  errSaving: {id: 'leads.webhook.errSaving', defaultMessage: 'Error saving webhook'},
  errNetworkSave: {id: 'leads.webhook.errNetworkSave', defaultMessage: 'Network error saving webhook'},
  webhookDeleted: {id: 'leads.webhook.webhookDeleted', defaultMessage: 'Webhook deleted'},
  errDeleting: {id: 'leads.webhook.errDeleting', defaultMessage: 'Error deleting webhook'},
  errNetworkDelete: {id: 'leads.webhook.errNetworkDelete', defaultMessage: 'Network error deleting webhook'},
  toastRegenGoogle: {id: 'leads.webhook.toastRegenGoogle', defaultMessage: 'Token and key regenerated \u2014 update Google Ads settings'},
  errRegenToken: {id: 'leads.webhook.errRegenToken', defaultMessage: 'Error regenerating token'},
  errNetworkRegen: {id: 'leads.webhook.errNetworkRegen', defaultMessage: 'Network error regenerating token'},
  copyError: {id: 'leads.webhook.copyError', defaultMessage: 'Could not copy to clipboard'},
})

export const ModalGoogleLeadsWebhook: React.FC<Props> = ({isOpen, onClose, stage, configId, onChanged, canEdit}) => {
  const intl = useIntl()

  const stageNumericId = Number(parseId(stage.id))

  const [config, setConfig] = useState<WebhookConfig | null>(null)
  const [options, setOptions] = useState<Options | null>(null)
  const [loadingConfig, setLoadingConfig] = useState(false)
  const [loadingOptions, setLoadingOptions] = useState(false)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [regenerating, setRegenerating] = useState(false)
  const [copied, setCopied] = useState<CopyField>(null)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [confirmRegenerate, setConfirmRegenerate] = useState(false)
  const [activeTab, setActiveTab] = useState('webhook')
  const [showTagManager, setShowTagManager] = useState(false)
  const [viewLeadInfo, setViewLeadInfo] = useState<IInfoLead | null>(null)

  // Form state
  const [categoryId, setCategoryId] = useState('')
  const [leadSourceId, setLeadSourceId] = useState('')
  const [campusId, setCampusId] = useState('')
  const [typeLead, setTypeLead] = useState<'PERSONAL' | 'COMERCIAL'>('PERSONAL')
  const [isActive, setIsActive] = useState(true)
  const [description, setDescription] = useState('')
  const [allowTestLeads, setAllowTestLeads] = useState(false)
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([])

  const loadConfig = useCallback(async () => {
    if (!configId) {
      setConfig(null)
      setActiveTab('settings')
      return
    }
    setLoadingConfig(true)
    try {
      const res = await fetch(`${BACKEND_URL}/api/google-leads/webhook/config/${configId}/`, {
        headers: authHeaders(),
      })
      if (res.status === 404) {
        setConfig(null)
        setActiveTab('settings')
      } else if (res.ok) {
        const data: WebhookConfig = await res.json()
        setConfig(data)
        setActiveTab('webhook')
        setLeadSourceId(data.lead_source_id || '')
        setCampusId(data.campus_id || '')
        setTypeLead(data.type_lead)
        setIsActive(data.is_active)
        setDescription(data.description || '')
        setAllowTestLeads(!!data.allow_test_leads)
        setSelectedTagIds(data.tags.map((t) => t.id))
      } else {
        toast.error(intl.formatMessage(wM.errLoadConfig))
      }
    } catch {
      toast.error(intl.formatMessage(wM.errNetworkLoad))
    } finally {
      setLoadingConfig(false)
    }
  }, [configId, intl])

  const loadOptions = useCallback(async () => {
    setLoadingOptions(true)
    try {
      const res = await fetch(`${BACKEND_URL}/api/google-leads/webhook/options/`, {
        headers: authHeaders(),
      })
      if (res.ok) {
        const data: Options = await res.json()
        setOptions(data)
      }
    } catch {
      // non-blocking
    } finally {
      setLoadingOptions(false)
    }
  }, [])

  useEffect(() => {
    if (!isOpen) {
      setConfig(null)
      setOptions(null)
      setCategoryId('')
      setLeadSourceId('')
      setCampusId('')
      setTypeLead('PERSONAL')
      setIsActive(true)
      setDescription('')
      setAllowTestLeads(false)
      setSelectedTagIds([])
      setConfirmDelete(false)
      setConfirmRegenerate(false)
      setActiveTab('settings')
      return
    }
    loadConfig()
    loadOptions()
  }, [isOpen, loadConfig, loadOptions])

  // Resolve category from loaded config's lead_source_id
  useEffect(() => {
    if (!config?.lead_source_id || !options) return
    for (const cat of options.categories) {
      if (cat.sources.some((s) => s.id === config.lead_source_id)) {
        setCategoryId(cat.id)
        break
      }
    }
  }, [config, options])

  const filteredSources =
    options?.categories.find((c) => c.id === categoryId)?.sources || []

  const toggleTag = (id: string) => {
    setSelectedTagIds((prev) =>
      prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id]
    )
  }

  const handleSave = async () => {
    if (!leadSourceId) {
      toast.warning(intl.formatMessage(wM.selectLeadSourceWarn))
      return
    }
    if (!campusId) {
      toast.warning(intl.formatMessage(wM.selectCampusWarn))
      return
    }
    setSaving(true)
    try {
      const existingId = config?.id ?? configId
      const url = existingId
        ? `${BACKEND_URL}/api/google-leads/webhook/config/${existingId}/`
        : `${BACKEND_URL}/api/google-leads/webhook/${stageNumericId}/`
      const res = await fetch(url, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          lead_source_id: leadSourceId,
          campus_id: campusId,
          type_lead: typeLead,
          is_active: isActive,
          description: description.trim(),
          allow_test_leads: allowTestLeads,
          tag_ids: selectedTagIds,
        }),
      })
      if (res.ok) {
        const data: WebhookConfig = await res.json()
        const wasNew = !config
        setConfig(data)
        setSelectedTagIds(data.tags.map((t) => t.id))
        toast.success(wasNew ? intl.formatMessage(wM.webhookCreated) : intl.formatMessage(wM.webhookUpdated))
        if (wasNew) setActiveTab('webhook')
        onChanged?.()
      } else {
        const err = await res.json()
        toast.error(err?.error || intl.formatMessage(wM.errSaving))
      }
    } catch {
      toast.error(intl.formatMessage(wM.errNetworkSave))
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!configId) return
    setDeleting(true)
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/google-leads/webhook/config/${configId}/delete/`,
        {method: 'DELETE', headers: authHeaders()}
      )
      if (res.ok) {
        setConfig(null)
        setConfirmDelete(false)
        toast.success(intl.formatMessage(wM.webhookDeleted))
        onChanged?.()
        onClose()
      } else {
        toast.error(intl.formatMessage(wM.errDeleting))
      }
    } catch {
      toast.error(intl.formatMessage(wM.errNetworkDelete))
    } finally {
      setDeleting(false)
    }
  }

  const handleRegenerate = async () => {
    if (!configId) return
    setRegenerating(true)
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/google-leads/webhook/config/${configId}/regenerate/`,
        {method: 'POST', headers: authHeaders()}
      )
      if (res.ok) {
        const data: WebhookConfig = await res.json()
        setConfig(data)
        setConfirmRegenerate(false)
        toast.success(intl.formatMessage(wM.toastRegenGoogle))
      } else {
        toast.error(intl.formatMessage(wM.errRegenToken))
      }
    } catch {
      toast.error(intl.formatMessage(wM.errNetworkRegen))
    } finally {
      setRegenerating(false)
    }
  }

  const copyToClipboard = async (text: string, field: CopyField) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(field)
      setTimeout(() => setCopied(null), 2000)
    } catch {
      toast.error(intl.formatMessage(wM.copyError))
    }
  }

  const isLoading = loadingConfig || loadingOptions

  return (
    <Modal
      show={isOpen}
      onHide={onClose}
      size='lg'
      centered
      className='webhook-modal-dialog'
      backdropClassName='webhook-modal-backdrop'
    >
      <Modal.Header closeButton>
        <div className='d-flex align-items-center justify-content-between w-100 me-2'>
          <Modal.Title className='d-flex align-items-center gap-2'>
            <svg width='20' height='20' viewBox='0 0 48 48' fill='none' xmlns='http://www.w3.org/2000/svg'>
              <path d='M43.6 20.2H24v7.2h11.3c-1.1 5.4-5.8 9.2-11.3 9.2-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.7 1.1 7.8 2.9l5.1-5.1C33.5 7.3 29 5.6 24 5.6 13.3 5.6 4.6 14.3 4.6 25S13.3 44.4 24 44.4c11 0 20.4-8 20.4-19.4 0-1.3-.1-2.6-.3-3.8z' fill='#4285F4'/>
              <path d='M6.3 15.1l6 4.4c1.6-4.5 5.8-7.7 11-7.7 3 0 5.7 1.1 7.8 2.9l5.1-5.1C33.5 7.3 29 5.6 24 5.6c-7.5 0-14 4.3-17.7 10.5z' fill='#EA4335'/>
              <path d='M24 44.4c5 0 9.4-1.7 12.8-4.5l-5.9-5c-1.8 1.3-4.1 2-6.9 2-5.5 0-10.2-3.7-11.9-8.9l-6 4.6C7.9 40.1 15.4 44.4 24 44.4z' fill='#34A853'/>
              <path d='M43.6 20.2H24v7.2h11.3c-.5 2.5-1.9 4.7-3.9 6.2l5.9 5c3.4-3.2 5.7-7.9 5.7-13.4 0-1.3-.1-2.6-.3-3.8z' fill='#FBBC04'/>
            </svg>
            {intl.formatMessage(wM.googleTitle)} {emdash} {stage.name}
          </Modal.Title>
          {config && (
            <Form.Check
              type='switch'
              id='header-webhook-active-toggle'
              label={
                <span className='fw-semibold' style={{fontSize: '0.85rem'}}>
                  {intl.formatMessage(wM.webhookActive)}
                </span>
              }
              checked={isActive}
              disabled={!canEdit}
              onChange={(e) => setIsActive(e.target.checked)}
              className='mb-0'
            />
          )}
        </div>
      </Modal.Header>

      <Modal.Body className='p-0'>
        {isLoading ? (
          <div className='text-center py-5'>
            <Spinner animation='border' />
          </div>
        ) : (
          <>
            <div className='px-3 pt-3'>
              {!canEdit && (
                <Alert variant='secondary' className='py-2 mb-3'>
                  <i className='bi bi-eye me-2' />
                  <small>{intl.formatMessage(wM.readOnly)}</small>
                </Alert>
              )}
              <Form.Group className='mb-0'>
                <Form.Label className='fw-bold'>{intl.formatMessage(wM.description)}</Form.Label>
                <Form.Control
                  type='text'
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={intl.formatMessage(wM.descriptionPlaceholder)}
                  disabled={!canEdit}
                />
              </Form.Group>
            </div>
            <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k || 'webhook')}>
            {/* Tab navigation */}
            <div className='border-bottom px-3 pt-3'>
              <Nav variant='tabs' className='border-bottom-0'>
                <Nav.Item>
                  <Nav.Link eventKey='webhook'>
                    <i className='bi bi-link-45deg me-1' />
                    Webhook
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey='settings'>
                    <i className='bi bi-sliders me-1' />
                    {intl.formatMessage(wM.updateSettings)}
                  </Nav.Link>
                </Nav.Item>
              </Nav>
            </div>

            <Tab.Content>
              {/* ── Tab 1: Webhook ── */}
              <Tab.Pane eventKey='webhook' className='p-3'>
                {config && (
                  <div className='mb-4'>
                    <div className='d-flex align-items-center gap-2 mb-3'>
                      <Badge bg='success' className='fs-6'>
                        <i className='bi bi-check-circle me-1' />
                        Webhook configured
                      </Badge>
                      <Badge bg={config.is_active ? 'success' : 'secondary'}>
                        {config.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>

                    <Alert variant='info' className='py-2 mb-3'>
                      <small>{intl.formatMessage(wM.googleInstructions)}</small>
                    </Alert>

                    {/* Webhook URL */}
                    <Form.Group className='mb-3'>
                      <Form.Label className='fw-bold'>{intl.formatMessage(wM.webhookUrl)}</Form.Label>
                      <div className='input-group'>
                        <Form.Control
                          type='text'
                          readOnly
                          value={config.webhook_url}
                          className='font-monospace'
                          style={{fontSize: '0.8rem'}}
                        />
                        <Button
                          variant={copied === 'url' ? 'success' : 'outline-secondary'}
                          onClick={() => copyToClipboard(config.webhook_url, 'url')}
                        >
                          <i className={`bi ${copied === 'url' ? 'bi-check-lg' : 'bi-clipboard'}`} />
                        </Button>
                      </div>
                    </Form.Group>

                    {/* Google Key */}
                    <Form.Group className='mb-4'>
                      <Form.Label className='fw-bold'>{intl.formatMessage(wM.keyGoogleField)}</Form.Label>
                      <div className='input-group'>
                        <Form.Control
                          type='text'
                          readOnly
                          value={config.google_key}
                          className='font-monospace'
                        />
                        <Button
                          variant={copied === 'key' ? 'success' : 'outline-secondary'}
                          onClick={() => copyToClipboard(config.google_key, 'key')}
                        >
                          <i className={`bi ${copied === 'key' ? 'bi-check-lg' : 'bi-clipboard'}`} />
                        </Button>
                      </div>
                    </Form.Group>

                    {/* Stats */}
                    <div className='d-flex gap-3 mb-4'>
                      <div className='border rounded p-2 flex-fill text-center'>
                        <div className='fw-bold fs-4'>{config.leads_count}</div>
                        <small className='text-muted'>{intl.formatMessage(wM.leadsReceived)}</small>
                      </div>
                      <div className='border rounded p-2 flex-fill text-center'>
                        <div className='fw-bold d-flex align-items-center justify-content-center gap-1' style={{fontSize: '0.85rem'}}>
                          {config.last_triggered_at
                            ? new Date(config.last_triggered_at).toLocaleString()
                            : emdash}
                          {config.last_lead_id && (
                            <Button
                              variant='link'
                              size='sm'
                              className='p-0'
                              title={intl.formatMessage(wM.viewLastLead)}
                              onClick={() => setViewLeadInfo({id: String(config.last_lead_id), name: 'Lead'})}
                            >
                              <i className='bi bi-box-arrow-up-right' />
                            </Button>
                          )}
                        </div>
                        <small className='text-muted'>{intl.formatMessage(wM.lastTriggered)}</small>
                      </div>
                      <div className='border rounded p-2 flex-fill text-center'>
                        <Badge bg={config.is_active ? 'success' : 'secondary'} className='fs-6'>
                          {config.is_active ? intl.formatMessage({id: 'common.status.active', defaultMessage: 'Active'}) : intl.formatMessage({id: 'common.status.inactive', defaultMessage: 'Inactive'})}
                        </Badge>
                        <br />
                        <small className='text-muted'>{intl.formatMessage(wM.statusLabel)}</small>
                      </div>
                    </div>

                    <hr />
                  </div>
                )}

                {/* Danger zone — regenerar y borrar exigen `change_`/`delete_` */}
                {config && canEdit && (
                  <div className='border border-warning rounded p-3 mt-4'>
                    <p className='fw-bold mb-2 text-warning'>
                      <i className='bi bi-exclamation-triangle me-2' />
                      {intl.formatMessage(wM.dangerZone)}
                    </p>

                    {!confirmRegenerate ? (
                      <Button
                        variant='outline-warning'
                        size='sm'
                        className='me-2'
                        onClick={() => setConfirmRegenerate(true)}
                      >
                        <i className='bi bi-arrow-repeat me-1' />
                        {intl.formatMessage(wM.regenTokenKey)}
                      </Button>
                    ) : (
                      <div className='d-flex align-items-center gap-2 mb-2'>
                        <small className='text-warning'>{intl.formatMessage(wM.regenWarnGoogle)}</small>
                        <Button
                          variant='warning'
                          size='sm'
                          onClick={handleRegenerate}
                          disabled={regenerating}
                        >
                          {regenerating ? <Spinner size='sm' animation='border' /> : intl.formatMessage(wM.confirmRegenerate)}
                        </Button>
                        <Button variant='link' size='sm' onClick={() => setConfirmRegenerate(false)}>
                          <FormattedMessage id='common.action.cancel' defaultMessage='Cancel' />
                        </Button>
                      </div>
                    )}

                    {!confirmDelete ? (
                      <Button variant='outline-danger' size='sm' className='mt-2' onClick={() => setConfirmDelete(true)}>
                        <i className='bi bi-trash me-1' />
                        {intl.formatMessage(wM.deleteWebhook)}
                      </Button>
                    ) : (
                      <div className='d-flex align-items-center gap-2 mt-2'>
                        <small className='text-danger'>{intl.formatMessage(wM.deleteConfirm)}</small>
                        <Button variant='danger' size='sm' onClick={handleDelete} disabled={deleting}>
                          {deleting ? <Spinner size='sm' animation='border' /> : intl.formatMessage(wM.yesDelete)}
                        </Button>
                        <Button variant='link' size='sm' onClick={() => setConfirmDelete(false)}>
                          <FormattedMessage id='common.action.cancel' defaultMessage='Cancel' />
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </Tab.Pane>

              {/* ── Tab 2: Settings ── */}
              <Tab.Pane eventKey='settings' className='p-3'>
                <h6 className='mb-3'>{config ? intl.formatMessage(wM.updateSettings) : intl.formatMessage(wM.createForStage)}</h6>

                {/* Lead Source — 2 levels */}
                <Form.Group className='mb-3'>
                  <Form.Label>{intl.formatMessage(wM.leadSourceCategory)}</Form.Label>
                  <Form.Select
                    value={categoryId}
                    onChange={(e) => {
                      setCategoryId(e.target.value)
                      setLeadSourceId('')
                    }}
                    disabled={loadingOptions || !canEdit}
                  >
                    <option value=''>{intl.formatMessage(wM.selectCategory)}</option>
                    {options?.categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Form.Group className='mb-3'>
                  <Form.Label><FormattedMessage id='leads.form.leadSource' defaultMessage='Lead source' /></Form.Label>
                  <Form.Select
                    value={leadSourceId}
                    onChange={(e) => setLeadSourceId(e.target.value)}
                    disabled={!categoryId || loadingOptions || !canEdit}
                  >
                    <option value=''>{intl.formatMessage(wM.selectSource)}</option>
                    {filteredSources.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                {/* Campus */}
                <Form.Group className='mb-3'>
                  <Form.Label>{intl.formatMessage(wM.campus)}</Form.Label>
                  <Form.Select
                    value={campusId}
                    onChange={(e) => setCampusId(e.target.value)}
                    disabled={loadingOptions || !canEdit}
                  >
                    <option value=''>{intl.formatMessage(wM.selectCampus)}</option>
                    {options?.campuses.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                {/* Type lead */}
                <Form.Group className='mb-3'>
                  <Form.Label>{intl.formatMessage(wM.leadType)}</Form.Label>
                  <div className='d-flex gap-3'>
                    <Form.Check
                      type='radio'
                      label={intl.formatMessage({id: 'leads.form.typePersonal', defaultMessage: 'Personal'})}
                      id='type-personal'
                      checked={typeLead === 'PERSONAL'}
                      disabled={!canEdit}
                      onChange={() => setTypeLead('PERSONAL')}
                    />
                    <Form.Check
                      type='radio'
                      label={intl.formatMessage({id: 'leads.form.typeCommercial', defaultMessage: 'Commercial'})}
                      id='type-comercial'
                      checked={typeLead === 'COMERCIAL'}
                      disabled={!canEdit}
                      onChange={() => setTypeLead('COMERCIAL')}
                    />
                  </div>
                </Form.Group>

                {/* Tags */}
                <Form.Group className='mb-3'>
                  <div className='d-flex align-items-center justify-content-between'>
                    <Form.Label className='mb-0'>{intl.formatMessage(wM.autoAssignTags)}</Form.Label>
                    <Button
                      variant='link'
                      size='sm'
                      className='p-0'
                      disabled={!canEdit}
                      onClick={() => setShowTagManager(true)}
                    >
                      <i className='bi bi-tags me-1' />
                      {intl.formatMessage(wM.manageTags)}
                    </Button>
                  </div>
                  {options && options.tags.length > 0 && (
                    <div className='d-flex flex-wrap gap-2 p-2 border rounded mt-1'>
                      {options.tags.map((tag) => {
                        const selected = selectedTagIds.includes(tag.id)
                        return (
                          <button
                            key={tag.id}
                            type='button'
                            disabled={!canEdit}
                            onClick={() => toggleTag(tag.id)}
                            style={{
                              background: selected ? (tag.color || '#6c757d') : 'transparent',
                              border: `2px solid ${tag.color || '#6c757d'}`,
                              borderRadius: '20px',
                              padding: '3px 12px',
                              fontSize: '0.82rem',
                              fontWeight: selected ? 600 : 400,
                              color: selected ? '#fff' : (tag.color || '#6c757d'),
                              cursor: 'pointer',
                              transition: 'all 0.15s',
                            }}
                          >
                            {selected && <i className='bi bi-check me-1' />}
                            {tag.name}
                          </button>
                        )
                      })}
                    </div>
                  )}
                  {selectedTagIds.length > 0 && (
                    <small className='text-muted mt-1 d-block'>{intl.formatMessage(wM.tagsSelected, {count: selectedTagIds.length})}</small>
                  )}
                </Form.Group>

                <Form.Group className='mb-3 pt-3 border-top'>
                  <Form.Check
                    type='switch'
                    id='allow-test-leads'
                    label={intl.formatMessage(wM.allowTestLeads)}
                    checked={allowTestLeads}
                    disabled={!canEdit}
                    onChange={(e) => setAllowTestLeads(e.target.checked)}
                  />
                  <span className='text-muted fs-8 d-block mt-1'>
                    {intl.formatMessage(wM.allowTestLeadsHint)}
                  </span>
                </Form.Group>
              </Tab.Pane>
            </Tab.Content>
            </Tab.Container>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant='secondary' onClick={onClose}>
          <FormattedMessage id='common.action.close' defaultMessage='Close' />
        </Button>
        {!isLoading && canEdit && (
          <Button variant='primary' onClick={handleSave} disabled={saving}>
            {saving ? (
              <>
                <Spinner size='sm' animation='border' className='me-2' />
                {intl.formatMessage(wM.saving)}
              </>
            ) : config ? (
              intl.formatMessage(wM.updateWebhook)
            ) : (
              intl.formatMessage(wM.createWebhook)
            )}
          </Button>
        )}
      </Modal.Footer>

      <LeadTagManager
        show={showTagManager}
        onHide={() => setShowTagManager(false)}
        onTagsUpdated={loadOptions}
      />

      {viewLeadInfo && (
        <DrawerEditLead
          isOpen={!!viewLeadInfo}
          onClose={() => setViewLeadInfo(null)}
          info={viewLeadInfo}
          updateListLeads={() => {}}
        />
      )}
    </Modal>
  )
}
